/* eslint-disable react-hooks/exhaustive-deps */
import { Socket } from "socket.io-client";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ButtonComponent,
  ButtonPayloadEnum,
  ChatResponse,
  MessageComponentResponse,
  MediaComponent,
  MessageContent,
  MessageStreamingResponse,
  LiveChatState,
  LinkPreviewComponent,
  SurveyComponent,
  ProductCarouselComponent,
} from "@aichat/types/socket/widget.types";
import { UserConversationRoleEnum } from "@aichat/types/enums/index";
import { defaultMessageContent, useGlobalStore } from "../store/globalStore";
import WavStreamPlayer from "@aichat/ui/lib/player";
import { createId } from "@paralleldrive/cuid2";
import { IFRAME_MESSAGES } from "../lib/constants";
import { useFlags } from "flagsmith/react";
import { submitSurvey } from "../api/customer-survey.api";
import { SubmitSurveyParams } from "@aichat/types/customer-survey.types";
import {
  ClientToServerEvents,
  ServerToClientEvents,
  FlagsmithTraits,
} from "@aichat/types/index";
export type LocalSocket = Socket<
  ServerToClientEvents,
  ClientToServerEvents
> | null;

type UseLocalSocket = {
  widgetSocket: LocalSocket;
  isConnected: boolean;
  conversationMedium?: "audio";
  organizationId: string;
  apiKey: string;
  isPlayground?: boolean;
  projectId: string;
};

// const AUDIO_SEND = "/sounds/sent_message.mp3";
// const AUDIO_RECEIVE = "/sounds/new_message.mp3";
const useLocalSocket = ({
  widgetSocket,
  isConnected,
  conversationMedium,
  organizationId,
  apiKey,
  projectId,
}: UseLocalSocket) => {
  // const sendAudioRef = useRef(new Audio(AUDIO_SEND));
  // const receiveAudioRef = useRef(new Audio(AUDIO_RECEIVE));
  const [welcomeContainerCookie, setWelcomeContainerCookie] = useState("false");
  const linkPreviewRef = useRef<LinkPreviewComponent[]>([]);
  const productCarouselRef = useRef<ProductCarouselComponent[]>([]);
  const mediaRef = useRef<MediaComponent[]>([]);
  const quickRepliesAsButtonRef = useRef<ButtonComponent[]>([]);
  const [customerSurvey, setCustomerSurvey] = useState<
    SurveyComponent["component"] | null
  >(null);
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(new WavStreamPlayer());
  const trackIdRef = useRef<string>("");
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceBotSpeaking, setIsVoiceBotSpeaking] = useState(false);
  const [voiceOverlay, setVoiceOverlay] =
    useState<MessageComponentResponse | null>(null);
  const [liveChatState, setLiveChatState] = useState(LiveChatState.NONE);

  const { digital_human: digitalHuman } = useFlags(
    [],
    [FlagsmithTraits.DIGITAL_HUMAN],
  );

  const {
    isStreaming,
    streamedMsgContent,
    chat,
    messages,
    conversationId,
    updateStreamedMsgContent: updateStreamedText,
    setMessages,
    setStreamedMsgContent,
    setChat,
    setIsStreaming,
    updateMessages,
    messageLoading,
    setSocketErrorMsg,
    socketErrorMsg,
    setMessageLoading,
    isLoading,
    setConversationId,
    chatMode,
    setChatMode,
  } = useGlobalStore((state) => state);

  const messageRef = useRef(messages);
  const streamedMsgContentRef = useRef(streamedMsgContent);
  messageRef.current = messages;
  // const playAudio = (audio: HTMLAudioElement) => {
  //   if (audio) {
  //     audio.currentTime = 0;
  //     audio.play().catch((error) => console.error("Audio Error:", error));
  //   }
  // };

  useEffect(() => {
    window.addEventListener("message", (event) => {
      const { type } = event.data;
      if (type === IFRAME_MESSAGES.cookieHasBeenSet) {
        const { cookieList } = event.data;
        const cookieWelcomeContainer =
          cookieList[`welcome-container-${organizationId}-${apiKey}`];
        setWelcomeContainerCookie(cookieWelcomeContainer);
      }
    });
  }, []);

  const onAudioComplete = useCallback(() => {
    setIsVoiceBotSpeaking(false);
    setIsRecording(true);
  }, []);

  const onAvatarStopTalking = useCallback(() => {
    onAudioComplete();
    setStreamedMsgContent(defaultMessageContent);
  }, [onAudioComplete]);

  const onAvatarStartTalking = useCallback(() => {
    setMessageLoading(false);
    updateMessages((prev) => {
      const messageExists = prev.some(
        ({ id }) => id === streamedMsgContentRef.current.id,
      );
      return messageExists ? prev : [...prev, streamedMsgContentRef.current];
    });
  }, []);

  useEffect(() => {
    if (isVoiceBotSpeaking && !isStreaming) {
      window.addEventListener("audio_completed", onAudioComplete);
      return () => {
        window.removeEventListener("audio_completed", onAudioComplete);
      };
    }
  }, [isStreaming, isVoiceBotSpeaking, onAudioComplete]);

  useEffect(() => {
    if (conversationMedium === "audio") {
      setIsRecording(true);
      wavStreamPlayerRef.current.connect().catch();

      return () => {
        setIsRecording(false);
        wavStreamPlayerRef.current.interrupt().catch();
        wavStreamPlayerRef.current.close();
      };
    }
  }, [conversationMedium]);

  useEffect(() => {
    if (!isVoiceBotSpeaking && conversationMedium === "audio") {
      wavStreamPlayerRef.current.close();
      wavStreamPlayerRef.current.connect().catch();
    }
  }, [conversationMedium, isVoiceBotSpeaking]);

  useEffect(() => {
    if (chat?.conversation.activeLiveChatAgentUserId === null) {
      setChatMode("bot");
    } else {
      setChatMode("live_chat_agent");
    }
  }, [chat?.conversation.activeLiveChatAgentUserId]);

  useEffect(() => {
    if (!widgetSocket || !isConnected) return;

    widgetSocket?.on("message", (newMessage) => {
      if (!newMessage) return;
      if (newMessage.content.system_message) {
        setLiveChatState(LiveChatState.NONE);
      }
      if (newMessage.role === UserConversationRoleEnum.LIVE_CHAT_AGENT)
        setChatMode("live_chat_agent");

      setMessageLoading(false);
      updateMessages((prev) => [...prev, newMessage]);

      if (!conversationId) return;
    });

    widgetSocket.on("component", (response) => {
      switch (response.type) {
        case "link_preview":
          linkPreviewRef.current = [
            ...linkPreviewRef.current,
            ...response.data,
          ];
          return;
        case "product_carousel":
          productCarouselRef.current = [
            ...productCarouselRef.current,
            ...response.data,
          ];
          return;
        case "media":
          mediaRef.current = [...mediaRef.current, response];
          return;
        case "quick_reply": {
          const quickReplyAsButton: ButtonComponent = {
            id: Date.now().toString(),
            label: response.label,
            payload: {
              type: ButtonPayloadEnum.REPLY,
              value: response.payload,
            },
          };
          quickRepliesAsButtonRef.current = [
            ...quickRepliesAsButtonRef.current,
            quickReplyAsButton,
          ];
          return;
        }
        case "survey":
          setCustomerSurvey(response.component);
          return;
      }
    });

    widgetSocket?.on("widget:chat_history", (response: ChatResponse) => {
      const { conversation, cookieName, expiresIn, messages, tokenValue } =
        response;

      const widgetCookieKey = `${cookieName}-${organizationId}-${apiKey}`;

      setMessages(messages);
      setChat(response);
      setConversationId(conversation.id);
      // move Cookies.set into iframe
      window.parent.postMessage(
        {
          type: IFRAME_MESSAGES.setCookieData,
          conversationMedium: conversationMedium,
          widgetCookieKey: widgetCookieKey,
          tokenValue: tokenValue,
          expiresIn: expiresIn,
          organizationId: organizationId,
          cookieName: cookieName,
        },
        "*",
      );
    });

    widgetSocket?.on("message_stream", (response: MessageStreamingResponse) => {
      if (!response) return;
      if (digitalHuman) return;
      try {
        setIsStreaming(true);
        setMessageLoading(false);
        updateStreamedText((prev) => {
          return {
            ...prev,
            content: {
              text: `${prev.content.text}${response}`,
            },
          };
        });
      } catch (error) {
        console.error(error);
      }
    });

    widgetSocket?.on(
      "audio_stream",
      async (response: MessageStreamingResponse) => {
        if (!response) return;
        function onAudioStart() {
          setIsRecording(false);
          setIsVoiceBotSpeaking(true);
          setIsStreaming(true);
          setMessageLoading(false);
        }
        try {
          const buffer = wavStreamPlayerRef.current.add16BitPCM(
            Uint8Array.from(atob(response), (c) => c.charCodeAt(0)).buffer,
            trackIdRef.current,
          );
          if (buffer) onAudioStart();
        } catch (error) {
          console.error(error);
        }
      },
    );

    widgetSocket?.on("message_stream_end", (newMessage: MessageContent) => {
      if (!newMessage) return;
      if (!digitalHuman) {
        setIsStreaming(false);
        setMessageLoading(false);
      }
      const message = {
        ...newMessage,
        content: {
          ...newMessage.content,
          ...(linkPreviewRef.current.length && {
            link_preview: linkPreviewRef.current,
          }),
          ...(productCarouselRef.current.length && {
            product_carousel: productCarouselRef.current,
          }),
          ...(quickRepliesAsButtonRef.current.length && {
            buttons: quickRepliesAsButtonRef.current,
          }),
          ...(mediaRef.current && { medias: mediaRef.current }),
        },
      };
      setStreamedMsgContent(digitalHuman ? message : defaultMessageContent);
      updateMessages((prev) => {
        if (digitalHuman) {
          if (streamedMsgContentRef.current.id) {
            const messageExists = prev.some(
              ({ id }) => id === streamedMsgContentRef.current.id,
            );
            return messageExists
              ? prev
              : [...prev, streamedMsgContentRef.current];
          } else {
            return prev.length > 0 ? prev : [message];
          }
        } else {
          return [...prev, message];
        }
      });
      streamedMsgContentRef.current = message;
      // playAudio(receiveAudioRef.current);
      resetComponentRef();
      // Sometimes only text message is being sent in audio mode
      if (conversationMedium === "audio") {
        setIsVoiceBotSpeaking((prevState) => {
          if (!prevState) {
            setIsRecording(true);
          }
          return prevState;
        });
      }

      if (!conversationId) return;
    });

    widgetSocket?.on("error", ({ message }) => {
      setIsVoiceBotSpeaking(false);
      setIsRecording(true);
      setIsStreaming(false);
      setMessageLoading(false);

      // Using ostrich ignore for now
      // Forbidden error occurs from backend because ChannelId (apiKey) is undefined, which is not supposed to happen
      // Until the issue is fixed, we will ignore the error
      // TODO: Remove this and use proper error handling
      if (message.toLowerCase() !== "forbidden") setSocketErrorMsg(message);
      setStreamedMsgContent(streamedMsgContent);
    });

    widgetSocket.on("live_chat:end", () => {
      setLiveChatState(LiveChatState.SHOULD_END);
    });

    widgetSocket?.on("exception", (response) => {
      setIsVoiceBotSpeaking(false);
      setIsRecording(true);
      setIsStreaming(false);
      setMessageLoading(false);

      // Using ostrich ignore for now
      // Forbidden error occurs from backend because ChannelId (apiKey) is undefined, which is not supposed to happen
      // Until the issue is fixed, we will ignore the error
      // TODO: Remove this and use proper error handling
      if (response.message.toLowerCase() !== "forbidden")
        setSocketErrorMsg(response.message);
      setStreamedMsgContent(streamedMsgContent);
    });

    widgetSocket.on("agent:typing", ({ typing }) => {
      console.log("AGENT TYPING", typing);
      setMessageLoading(typing);
    });

    return () => {
      setMessageLoading(false);
      setIsStreaming(false);
      widgetSocket.removeAllListeners();
    };
  }, [
    widgetSocket,
    isConnected,
    organizationId,
    apiKey,
    conversationMedium,
    digitalHuman,
  ]);

  useEffect(() => {
    // If stream message content's text is empty, return
    if (!streamedMsgContent.content.text) return;
    const msgList = document.querySelector("#messages-wrapper");
    const lastMessageIdx = messageRef.current?.length;
    const lastMessageElement = document.querySelector(
      `#ai-chat-bubble-${lastMessageIdx - 1}`,
    );

    if (msgList && lastMessageElement) {
      // Get the position of the last message relative to the message wrapper
      lastMessageElement.classList.add("animate-scale-up");
      const lastMessagePosition =
        (lastMessageElement as HTMLElement).offsetTop +
        (lastMessageElement as HTMLElement).offsetHeight -
        40;

      msgList.scrollTo({
        top: lastMessagePosition,
        behavior: "smooth",
      });
    }
  }, [streamedMsgContent]);

  const resetComponentRef = () => {
    productCarouselRef.current = [];
    linkPreviewRef.current = [];
    mediaRef.current = [];
    quickRepliesAsButtonRef.current = [];
  };

  const onSendMessage = async (text: string) => {
    if (!text) return;
    setLiveChatState(LiveChatState.NONE);
    // playAudio(sendAudioRef.current);
    widgetSocket?.emit("message", {
      conversationId,
      role: UserConversationRoleEnum.CUSTOMER,
      message: {
        text: text.trim(),
      },
    });
    setMessageLoading(chatMode === "bot");
    setSocketErrorMsg(null);
    updateMessages((prev) => [
      ...prev,
      {
        role: UserConversationRoleEnum.CUSTOMER,
        content: {
          text,
        },
        createdAt: new Date().toISOString(),
      },
    ]);
    if (conversationMedium === "audio" || digitalHuman) {
      setIsRecording(false);
      trackIdRef.current = createId();
      setVoiceOverlay(null);
    }
  };

  const onInterrupt = () => {
    wavStreamPlayerRef.current
      .interrupt()
      .then(() => {
        setIsVoiceBotSpeaking(false);
        setIsRecording(true);
      })
      .catch();
    if (isStreaming) {
      widgetSocket?.emit("widget:cancel_stream");
    }
  };

  const onOpenChat = (open: boolean) => {
    // send message to disable welcome cookie when the prev cookie=true
    if (open && welcomeContainerCookie == "true") {
      window.parent.postMessage(
        {
          type: IFRAME_MESSAGES.disabledWelcomeMessage,
        },
        "*",
      );
    }
    setWelcomeContainerCookie("false");
  };

  const onCloseWelcome = () => {
    setWelcomeContainerCookie("false");
  };

  const submitCustomerSurvey = async ({
    close,
    ...payload
  }: SubmitSurveyParams) => {
    if (close) {
      setCustomerSurvey(null);
      return;
    }

    try {
      await submitSurvey({
        apiKey,
        orgId: organizationId,
        projectId,
        conversationId,
        ...payload,
      });
    } catch (error) {
      console.error("[Submit Survey]:", error);
    }
  };

  const handleCustomerTyping = (typing: boolean) => {
    widgetSocket?.emit("customer:typing", {
      conversationId,
      typing,
    });
  };

  const endLiveChat = () => {
    setLiveChatState(LiveChatState.ENDING);
    widgetSocket?.emit("agent:assign", {
      agentId: null,
      conversationId,
    });
    if (chat) {
      setChat({
        ...chat,
        conversation: {
          ...chat.conversation,
          activeLiveChatAgentUserId: null,
        },
      });
    }
  };

  return {
    onSendMessage,
    handleCustomerTyping,
    chat: chat,
    onOpenChat,
    onCloseWelcome,
    streamedMsgContent,
    showWelcomeContainer: welcomeContainerCookie !== "false",
    isStreaming,
    messages,
    customerSurvey,
    messageLoading,
    isLoading,
    socketErrorMsg,
    onInterrupt,
    isVoiceBotSpeaking,
    isRecording,
    setIsRecording,
    voiceOverlay,
    setVoiceOverlay,
    onAudioComplete,
    onAvatarStartTalking,
    onAvatarStopTalking,
    submitCustomerSurvey,
    liveChatState,
    setLiveChatState,
    endLiveChat,
    updateMessages,
  };
};

export default useLocalSocket;
